import React, { KeyboardEvent } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/keyMetrics";
import "./SortableKpiItem.scss";
import LeafCheckBox from "../../leaf/LeafCheckBox/LeafCheckBox";
import { IconList } from "../../icons/IconList/IconList";
import { PV_TYPE_MAP } from "../../../components/LandingPage/PerformanceIncomeDashboard/PerfIncomeKpiTile/PerIncomeKpiItemTile/PerfIncomeKpiItemTile";

type Props = {
  kpiID: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (kpi: string, e: React.KeyboardEvent) => void;
  textOverride?: { [key: string]: string };
  personalPVRequirementCode?: string;
};

const KpiItem = ({
  kpi,
  checked,
  disabled,
  isDragging,
  onChange,
  textOverride,
  personalPVRequirementCode,
  dragHandleListeners,
  setActivatorNodeRef,
}: {
  kpi: string;
  checked: boolean;
  disabled: boolean;
  isDragging?: boolean;
  onChange: (kpi: string, e: React.KeyboardEvent) => void;
  textOverride?: { [key: string]: string };
  personalPVRequirementCode?: string;
  dragHandleListeners?: ReturnType<typeof useSortable>["listeners"];
  setActivatorNodeRef?: ReturnType<typeof useSortable>["setActivatorNodeRef"];
}) => {
  const { t } = useTranslation(["keyMetrics"]);
  const overrideText = textOverride && Object.keys(textOverride).length > 0 ? textOverride[kpi] || "" : "";

  let titleKey = kpi;
  if (kpi === "personalPVRequirement") {
    titleKey = (personalPVRequirementCode && PV_TYPE_MAP[personalPVRequirementCode]) || "ppvTarget";
  }

  const defaultLabel = t(`${titleKey}`, defaultContent[titleKey]);

  const displayLabel = overrideText || defaultLabel;

  // Stop Space/Enter propagation so dnd-kit's KeyboardSensor does not
  // intercept these keys when focus is on the checkbox input.
  const handleCheckboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") {
      e.stopPropagation();
    }
  };

  return (
    <div className={`KpiItem ${isDragging ? "KpiItem--dragging" : ""} ${disabled ? "KpiItem--disabled" : ""}`}>
      <span
        className={`KpiItem__DragHandle ${disabled ? "KpiItem__DragHandle--disabled" : ""}`}
        tabIndex={disabled ? -1 : 0}
        ref={(node) => {
          if (setActivatorNodeRef) setActivatorNodeRef(node);
        }}
        {...(!disabled ? dragHandleListeners : {})}
        role="button"
        aria-label="Drag to reorder"
      >
        <IconList />
      </span>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyDown={handleCheckboxKeyDown}>
        <LeafCheckBox
          id={kpi.toString()}
          key={kpi}
          label={displayLabel}
          checked={checked}
          disabled={disabled}
          onChange={(e: KeyboardEvent<Element>) => onChange(kpi, e)}
          theme="blue"
        />
      </div>
    </div>
  );
};

export const SortableKpiItem = (props: Props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props.kpiID,
    disabled: props.disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Destructure role and tabIndex from attributes so the wrapper div is not
  // an interactive element (avoids nested-interactive a11y violation with the
  // LeafCheckBox inside). The drag handle span receives these instead.
  const {
    role: _role,
    tabIndex: _tabIndex,
    ...ariaAttributes
  } = attributes as React.HTMLAttributes<HTMLElement> & { role?: string; tabIndex?: number };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`SortableKpiItem ${isDragging ? "SortableKpiItem--dragging" : ""}`}
      {...(props.disabled ? {} : ariaAttributes)}
    >
      <KpiItem
        kpi={props.kpiID}
        checked={props.checked}
        disabled={props.disabled || false}
        onChange={props.onChange}
        textOverride={props.textOverride}
        personalPVRequirementCode={props.personalPVRequirementCode}
        dragHandleListeners={props.disabled ? undefined : listeners}
        setActivatorNodeRef={props.disabled ? undefined : setActivatorNodeRef}
      />
    </div>
  );
};

export { KpiItem };
